"use client";

import type { CSSProperties } from "react";
import { useTranslations } from "next-intl";
import {
  PULL_TO_REFRESH_MARK_SPIN_CLASS,
  PULL_TO_REFRESH_READY_POP_CLASS,
  getPullToRefreshIndicatorState,
  type PullToRefreshStatus,
} from "@award-party/shared";

const RING_RADIUS = 22;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function statusMessageKey(status: PullToRefreshStatus): "refreshing" | "release" | "pull" {
  if (status === "refreshing") {
    return "refreshing";
  }
  if (status === "ready") {
    return "release";
  }
  return "pull";
}

export function PullToRefreshIndicator({
  status,
  offset,
  progress,
}: {
  status: PullToRefreshStatus;
  offset: number;
  progress: number;
}) {
  const t = useTranslations("PullToRefresh");
  const visible = status !== "idle";
  const clampedProgress = Math.max(0, Math.min(progress, 1));
  const dashOffset = RING_CIRCUMFERENCE * (1 - clampedProgress);
  const scale = 0.72 + Math.min(progress, 1) * 0.28;
  const indicator = getPullToRefreshIndicatorState(status);

  return (
    <div
      className="ap-ptr-indicator pointer-events-none fixed inset-x-0 z-0 flex items-center justify-center overflow-hidden"
      style={
        {
          "--ap-ptr-indicator-offset": `${Math.max(offset, 0)}px`,
          opacity: visible ? 1 : 0,
          transition: status === "pulling" ? "none" : "opacity 180ms ease",
        } as CSSProperties
      }
      aria-hidden={!visible}
    >
      <div
        className={`relative flex h-14 w-14 items-center justify-center ${
          indicator.armed ? PULL_TO_REFRESH_READY_POP_CLASS : ""
        }`}
        role={visible ? "status" : undefined}
        aria-live="polite"
        aria-label={visible ? t(statusMessageKey(status)) : undefined}
      >
        <span
          className={`ap-ptr-glow absolute inset-0 rounded-full ${
            indicator.glow ? "ap-ptr-glow-active" : ""
          }`}
          style={{ opacity: 0.25 + clampedProgress * 0.55 }}
        />
        <svg
          className="absolute inset-0 h-full w-full -rotate-90"
          viewBox="0 0 56 56"
          aria-hidden="true"
        >
          <circle
            cx="28"
            cy="28"
            r={RING_RADIUS}
            fill="none"
            stroke="rgba(201,168,76,0.18)"
            strokeWidth="2"
          />
          <circle
            cx="28"
            cy="28"
            r={RING_RADIUS}
            fill="none"
            stroke="#c9a84c"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={dashOffset}
          />
        </svg>
        <span
          className={`ap-ptr-rays absolute inset-1 ${
            indicator.rays ? "ap-ptr-rays-active" : ""
          }`}
          aria-hidden="true"
        />
        <span className={indicator.spinLogo ? PULL_TO_REFRESH_MARK_SPIN_CLASS : undefined}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/images/logos/award-party/icon-gold.svg"
            alt=""
            width={28}
            height={28}
            className="relative h-7 w-7"
            style={{
              transform: indicator.spinLogo
                ? undefined
                : `scale(${scale}) rotate(${Math.min(progress, 1) * -18}deg)`,
              filter:
                indicator.glow
                  ? "drop-shadow(0 0 10px rgba(201,168,76,0.55))"
                  : "drop-shadow(0 0 4px rgba(201,168,76,0.25))",
            }}
          />
        </span>
      </div>
    </div>
  );
}
